import { TextareaAutosize } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  FaBold,
  FaCode,
  FaEye,
  FaEyeSlash,
  FaHeading,
  FaImage,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaMinus,
  FaQuoteRight,
  FaStrikethrough,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ModalGlobal } from '../modal/modal.Global';
import { TextMd } from './text.Md';

const ToolbarBtn = ({ title, onClick, children }) => (
  <button
    type='button'
    title={title}
    className='inline-flex items-center gap-05 rounded bg-main bg-opacity-40 px-05 py-03 text-sm text-contrast transition hover:bg-opacity-60'
    onMouseDown={event => {
      event.preventDefault();
      event.stopPropagation();
    }}
    onClick={onClick}
  >
    {children}
  </button>
);

export const MarkdownEditModal = ({
  idToOpen,
  title = 'Edition du texte',
  value,
  onChange,
  onSave,
  saving = false,
  message,
  error,
  saveLabel = 'Enregistrer',
  hideSave = false,
  onClose,
}) => {
  const isMobile = useSelector(state => state.global.isMobile);
  const [draft, setDraft] = useState(value || '');
  const [showPreview, setShowPreview] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('textSettings.showPreview') !== '0';
  });
  const textareaRef = useRef(null);
  const selectionRef = useRef({ start: 0, end: 0 });

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('textSettings.showPreview', showPreview ? '1' : '0');
  }, [showPreview]);

  const updateDraft = nextValue => {
    setDraft(nextValue);
    onChange?.(nextValue);
  };

  const applyTextareaEdit = editorFn => {
    const el = textareaRef.current;
    if (!el || typeof editorFn !== 'function') {
      return;
    }
    const start =
      typeof el.selectionStart === 'number'
        ? el.selectionStart
        : selectionRef.current.start || 0;
    const end =
      typeof el.selectionEnd === 'number'
        ? el.selectionEnd
        : selectionRef.current.end || start;
    const currentValue = typeof el.value === 'string' ? el.value : draft || '';
    const result = editorFn({
      value: currentValue,
      start,
      end,
      before: currentValue.slice(0, start),
      selected: currentValue.slice(start, end),
      after: currentValue.slice(end),
    });

    if (!result?.nextValue) {
      return;
    }

    updateDraft(result.nextValue);

    if (
      typeof result.nextSelectionStart === 'number' &&
      typeof result.nextSelectionEnd === 'number'
    ) {
      const applySelection = () => {
        const nextEl = textareaRef.current;
        if (!nextEl) return;
        nextEl.focus();
        nextEl.setSelectionRange(
          result.nextSelectionStart,
          result.nextSelectionEnd,
        );
        selectionRef.current = {
          start: result.nextSelectionStart,
          end: result.nextSelectionEnd,
        };
      };
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(applySelection);
      } else {
        applySelection();
      }
    }
  };

  const wrapSelection = (prefix, suffix, placeholder = '') => {
    applyTextareaEdit(({ before, selected, after, start, end }) => {
      const hasSelection = selected.length > 0;
      const content = hasSelection ? selected : placeholder;
      const nextValue = `${before}${prefix}${content}${suffix}${after}`;
      const selectionStart = start + prefix.length;
      return {
        nextValue,
        nextSelectionStart: selectionStart,
        nextSelectionEnd: hasSelection ? end + prefix.length : selectionStart + content.length,
      };
    });
  };

  const insertAtCursor = (insertion, caretOffset = insertion.length) => {
    applyTextareaEdit(({ before, after, start }) => ({
      nextValue: `${before}${insertion}${after}`,
      nextSelectionStart: start + caretOffset,
      nextSelectionEnd: start + caretOffset,
    }));
  };

  const prefixSelectedLines = prefix => {
    applyTextareaEdit(({ value: currentValue, start, end }) => {
      const lineStart = currentValue.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
      const lineEndRaw = currentValue.indexOf('\n', end);
      const lineEnd = lineEndRaw === -1 ? currentValue.length : lineEndRaw;
      const block = currentValue.slice(lineStart, lineEnd);
      const nextBlock = block
        .split('\n')
        .map(line => (line ? `${prefix}${line}` : line))
        .join('\n');
      const delta = nextBlock.length - block.length;
      return {
        nextValue:
          currentValue.slice(0, lineStart) + nextBlock + currentValue.slice(lineEnd),
        nextSelectionStart: start + (start === lineStart ? prefix.length : 0),
        nextSelectionEnd: end + delta,
      };
    });
  };

  const insertLink = () => {
    applyTextareaEdit(({ before, selected, after, start }) => {
      const label = selected || 'texte';
      const insertion = `[${label}](url)`;
      const urlStart = start + insertion.lastIndexOf('(url)') + 1;
      return {
        nextValue: `${before}${insertion}${after}`,
        nextSelectionStart: urlStart,
        nextSelectionEnd: urlStart + 3,
      };
    });
  };

  const insertImage = () => {
    applyTextareaEdit(({ before, selected, after, start }) => {
      const alt = selected || 'image';
      const insertion = `![${alt}](url)`;
      const urlStart = start + insertion.lastIndexOf('(url)') + 1;
      return {
        nextValue: `${before}${insertion}${after}`,
        nextSelectionStart: urlStart,
        nextSelectionEnd: urlStart + 3,
      };
    });
  };

  const rememberSelection = event => {
    selectionRef.current = {
      start: event.currentTarget.selectionStart ?? 0,
      end: event.currentTarget.selectionEnd ?? 0,
    };
  };

  return (
    <ModalGlobal
      title={title}
      idToOpen={idToOpen}
      width={isMobile ? undefined : showPreview ? 980 : 720}
      maxWidthClass={isMobile ? undefined : 'max-w-none'}
      onClose={onClose}
    >
      <div className='w-full'>
        <div className='relative mb-05 flex flex-wrap items-center gap-03'>
          {!isMobile && (
            <ToolbarBtn
              title={showPreview ? "Masquer l'aperçu" : "Afficher l'aperçu"}
              onClick={() => setShowPreview(prev => !prev)}
            >
              {showPreview ? <FaEyeSlash /> : <FaEye />}
              <span className='text-xs opacity-80'>aperçu</span>
            </ToolbarBtn>
          )}
          <ToolbarBtn
            title='Gras'
            onClick={() => wrapSelection('**', '**', 'texte')}
          >
            <FaBold />
          </ToolbarBtn>
          <ToolbarBtn
            title='Italique'
            onClick={() => wrapSelection('*', '*', 'texte')}
          >
            <FaItalic />
          </ToolbarBtn>
          <ToolbarBtn
            title='Barré'
            onClick={() => wrapSelection('~~', '~~', 'texte')}
          >
            <FaStrikethrough />
          </ToolbarBtn>
          <ToolbarBtn
            title='Titre'
            onClick={() => prefixSelectedLines('# ')}
          >
            <FaHeading />
          </ToolbarBtn>
          <ToolbarBtn
            title='Liste'
            onClick={() => prefixSelectedLines('- ')}
          >
            <FaListUl />
          </ToolbarBtn>
          <ToolbarBtn
            title='Liste numérotée'
            onClick={() => prefixSelectedLines('1. ')}
          >
            <FaListOl />
          </ToolbarBtn>
          <ToolbarBtn
            title='Citation'
            onClick={() => prefixSelectedLines('> ')}
          >
            <FaQuoteRight />
          </ToolbarBtn>
          <ToolbarBtn
            title='Code inline'
            onClick={() => wrapSelection('`', '`', 'code')}
          >
            <FaCode />
          </ToolbarBtn>
          <ToolbarBtn
            title='Bloc de code'
            onClick={() => insertAtCursor('```\n\n```', 4)}
          >
            <FaCode />
            <span className='text-xs opacity-80'>bloc</span>
          </ToolbarBtn>
          <ToolbarBtn
            title='Lien'
            onClick={insertLink}
          >
            <FaLink />
          </ToolbarBtn>
          <ToolbarBtn
            title='Image'
            onClick={insertImage}
          >
            <FaImage />
          </ToolbarBtn>
          <ToolbarBtn
            title='Séparateur'
            onClick={() => insertAtCursor('\n---\n', 5)}
          >
            <FaMinus />
          </ToolbarBtn>
        </div>

        <div
          className={
            isMobile || !showPreview
              ? 'mb-1 mt-1 w-full'
              : 'mb-1 mt-1 flex w-full gap-1'
          }
        >
          <div className={isMobile || !showPreview ? 'w-full' : 'min-w-0 flex-1'}>
            <TextareaAutosize
              ref={textareaRef}
              placeholder='Description markdown'
              minRows={10}
              className='block w-full rounded bg-main bg-opacity-40 p-1 text-contrast'
              value={draft}
              onChange={event => updateDraft(event.target.value)}
              onSelect={rememberSelection}
              onKeyUp={rememberSelection}
              onClick={rememberSelection}
              onBlur={rememberSelection}
            />
          </div>
          {!isMobile && showPreview && (
            <div className='min-w-0 flex-1 rounded bg-primary-d bg-opacity-20 p-1'>
              <div className='mb-05 text-xs font-bold uppercase tracking-wide text-contrast text-opacity-55'>
                Aperçu
              </div>
              {draft ? (
                <TextMd str={draft} />
              ) : (
                <div className='text-sm text-contrast text-opacity-60'>
                  Aucune description.
                </div>
              )}
            </div>
          )}
        </div>

        {message && (
          <div className='text-sm text-contrast text-opacity-75'>{message}</div>
        )}
        {error && <div className='text-sm text-secondary'>{error}</div>}
        {!hideSave && (
          <div className='mt-1 flex justify-end'>
            <button
              type='button'
              className='rounded bg-primary px-1 py-05 font-condensed text-sm font-bold uppercase text-contrast shadow disabled:opacity-50'
              disabled={saving}
              onClick={() => onSave?.(draft)}
            >
              {saving ? 'Enregistrement...' : saveLabel}
            </button>
          </div>
        )}
      </div>
    </ModalGlobal>
  );
};
